import { useEffect, useRef } from 'react';
import { animate, createScope, stagger } from 'animejs';

/**
 * useAnimeReveal — scroll-triggered animations using anime.js v4.
 *
 * Uses IntersectionObserver for one-time entrance animations and createScope()
 * for proper React integration and cleanup.
 *
 * @param {object} opts - Configuration options
 * @param {string} opts.selector - CSS selector for reveal elements (default: '[data-anime]')
 * @param {object} opts.rootRef - External React ref to use as the scope root (optional)
 */
function useAnimeReveal({ selector = '[data-anime]', rootRef: externalRootRef } = {}) {
  const internalRootRef = useRef(null);
  const rootRef = externalRootRef || internalRootRef;
  const scopeRef = useRef(null);

  useEffect(() => {
    if (!rootRef.current) return undefined;

    let observer;
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    scopeRef.current = createScope({ root: rootRef.current }).add(() => {
      const items = rootRef.current.querySelectorAll(selector);
      if (!items.length) return;

      const offsets = {
        up: { y: [38, 0] },
        down: { y: [-38, 0] },
        left: { x: [38, 0] },
        right: { x: [-38, 0] },
        scale: { scale: [0.94, 1] },
        fade: {},
      };

      const settleElement = (el) => {
        el.dataset.animeRevealed = 'true';
        el.classList.add('is-anime-visible');
        el.style.opacity = '1';
        el.style.transform = 'none';
      };

      const revealElement = (el) => {
        if (el.dataset.animeRevealed === 'true') {
          return;
        }

        const direction = el.dataset.animeDirection || 'up';
        const staggerChildren = el.dataset.animeStagger === 'true';
        const delay = parseInt(el.dataset.animeDelay || '0', 10);
        const animProps = offsets[direction] || offsets.up;

        settleElement(el);

        if (reducedMotion) {
          Array.from(el.children).forEach(settleElement);
          return;
        }

        if (staggerChildren && el.children.length > 0) {
          const children = Array.from(el.children);

          animate(children, {
            opacity: [0.9, 1],
            ...animProps,
            duration: 620,
            delay: stagger(70, { start: delay }),
            ease: 'out(3)',
          });

          return;
        }

        animate(el, {
          opacity: [0.92, 1],
          ...animProps,
          duration: 700,
          delay,
          ease: 'out(3)',
        });
      };

      if (reducedMotion || !('IntersectionObserver' in window)) {
        items.forEach(revealElement);
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.intersectionRatio <= 0) {
              return;
            }

            const el = entry.target;
            revealElement(el);
            observer.unobserve(el);
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -12% 0px',
          threshold: 0.12,
        }
      );

      items.forEach((el) => {
        observer.observe(el);
      });
    });

    return () => {
      observer?.disconnect();
      scopeRef.current?.revert();
    };
  }, [selector, rootRef]);

  return rootRef;
}

export default useAnimeReveal;
